import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";

import PreSCNFicMeetingSection from "./PreSCNFicMeetingSection";

import { PreSCNFicDecision, PreSCNFicMeeting, PreSCNFicMeetingUI } from "../../../../../src/api/preSCNFic";

import { useAppDispatch } from "../../../../../src/app/hooks";
import { useAppSelector } from "../../../../../src/app/store";

import { ErrorDisplay } from "../../../../../src/components/ui/error/ErrorDisplay";
import { CommentEntityType, CommentThreadType } from "../../../../api/comments";
import CommentMainPage from "../../../../features/comments/components/CommentMainPage";
import ReportActionHeader from "../common/ReportActionHeader";
import { ReportDetailsShimmering } from "../common/ReportDetailsShimmering";
import ReportHeader from "../common/ReportHeader";
import ViewOrAddRecommendation from "../common/ViewOrAddRecommendation";
import { getAllPreSCNFicMeetings, getPreSCNFicFetchError, getPreSCNFicFetchStatus } from "./logic/PreSCNFicSlice";
import { fetchPreSCNFicMeetings, savePreSCNFicMeetings } from "./logic/PreSCNFicThunk";
import { useCommitteeMembers, useConveners } from "../../../users/logic/hooks";

const PreSCNFicPage: React.FC = () => {
  const { id: case_id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [meetings, setMeetings] = useState<PreSCNFicMeetingUI[]>([]);

  const meetingsFromStore = useAppSelector(getAllPreSCNFicMeetings);
  const conveners = useConveners();
  const committeeMembers = useCommitteeMembers();
  const fetchStatus = useAppSelector(getPreSCNFicFetchStatus);
  const fetchError = useAppSelector(getPreSCNFicFetchError);
  const isPageLoading = fetchStatus === "loading";
  const isPageError = fetchStatus === "failed";

  const hasUnsavedNewRow = meetings.some((m) => m.isAdded);

  const isNewMeetingValid = (meeting: PreSCNFicMeetingUI) => {
    if (!meeting.isAdded) return true;

    return (
      !!meeting.convener &&
      !!meeting.name_of_committee?.trim() &&
      !!meeting.decision &&
      !!meeting.date_of_committee_meeting
    );
  };

  const areAllNewMeetingsValid = meetings.filter((m) => m.isAdded).every(isNewMeetingValid);

  const { scrollRef } = useOutletContext<{ scrollRef: React.RefObject<HTMLDivElement> }>();

  useEffect(() => {
    if (!case_id) return;

    dispatch(fetchPreSCNFicMeetings(case_id));
  }, [case_id, dispatch]);

  useEffect(() => {
    if (!meetingsFromStore) return;

    if (meetingsFromStore.length === 0) {
      const emptyMeeting: PreSCNFicMeetingUI = {
        meeting_id: "",
        convener: "",
        name_of_committee: "",
        members_of_committee: [],
        decision: undefined,
        remarks: "",
        date_of_committee_meeting: "",
        isDirty: true,
        isAdded: true,
      };

      setMeetings([emptyMeeting]);
      setIsDirty(true);
      return;
    }

    const formatted = meetingsFromStore.map((m) => ({
      ...m,
      isDirty: false,
      isAdded: false,
    }));

    setMeetings(formatted);
  }, [meetingsFromStore]);

  const handleAddMeeting = () => {
    const emptyMeeting: PreSCNFicMeetingUI = {
      meeting_id: "",
      convener: "",
      name_of_committee: "",
      members_of_committee: [],
      decision: undefined,
      remarks: "",
      date_of_committee_meeting: "",
      isDirty: true,
      isAdded: true,
    };

    setMeetings((prev) => [...prev, emptyMeeting]);
    setIsDirty(true);
  };

  const handleMeetingChange = (index: number, updatedMeeting: PreSCNFicMeeting) => {
    setMeetings((prev) => {
      const copy = [...prev];

      copy[index] = {
        ...updatedMeeting,
        isDirty: true,
        isAdded: copy[index].isAdded,
      };

      return copy;
    });

    setIsDirty(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const addedMeetings = meetings.filter((m) => m.isAdded);
      const updatedMeetings = meetings.filter((m) => m.isDirty && !m.isAdded);

      const add_requests = addedMeetings.map((m) => ({
        convener: m.convener,
        name_of_committee: m.name_of_committee,
        members_of_committee: m.members_of_committee,
        decision: m.decision,
        remarks: m.remarks ?? null,
        date_of_committee: m.date_of_committee_meeting!,
      }));

      const update_requests = updatedMeetings.map((m) => ({
        meeting_id: m.meeting_id,
        name_of_committee: m.name_of_committee,
        members_of_committee: m.members_of_committee,
        remarks: m.remarks ?? null,
      }));

      await dispatch(
        savePreSCNFicMeetings({
          case_id: case_id!,
          payload: {
            add_requests: add_requests.length ? add_requests : undefined,
            update_requests: update_requests.length ? update_requests : undefined,
          },
        }),
      ).unwrap();

      setIsDirty(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    const formatted = meetingsFromStore.map((m) => ({
      ...m,
      isDirty: false,
      isAdded: false,
    }));
    setMeetings(formatted);
    setIsDirty(false);
  };

  const handleRetry = () => {
    if (!case_id) return;
    dispatch(fetchPreSCNFicMeetings(case_id));
  };

  const hasSavedSCNMeeting = useMemo(() => {
    return meetings.some((m) => m.decision === PreSCNFicDecision.ISSUE_SCN && !m.isAdded);
  }, [meetings]);

  return (
    <div className="w-full h-full overflow-auto bg-[#F3F4F6]">
      <div className="sticky top-0 left-0 right-0 flex justify-end items-center px-6 py-4 bg-[#F3F4F6] z-10">
        <ReportActionHeader
          isPageLoading={isPageLoading}
          isSaving={isSaving}
          disableSave={!isDirty || !areAllNewMeetingsValid}
          disableDiscard={!isDirty}
          handleSave={handleSave}
          handleDiscard={handleDiscard}
        />
      </div>

      <div className="max-w-8xl mx-6 bg-white rounded-xl">
        {isPageLoading ? (
          <ReportDetailsShimmering sectionCount={2} />
        ) : isPageError ? (
          <ErrorDisplay
            error={fetchError}
            title="Failed to load Pre-SCN FIC Meetings"
            onRetry={handleRetry}
            className="m-1"
          />
        ) : (
          <>
            <ReportHeader title="Pre-SCN FIC" />

            <div className="px-6 py-4 min-h-[80vh]">
              {meetings.map((meeting, index) => (
                <PreSCNFicMeetingSection
                  key={meeting.meeting_id ?? index}
                  meeting={meeting}
                  onChange={(updated) => handleMeetingChange(index, updated)}
                  conveners={conveners}
                  committeeMembers={committeeMembers}
                  index={index}
                />
              ))}

              <div>
                <button
                  onClick={handleAddMeeting}
                  disabled={hasUnsavedNewRow}
                  className="flex items-center gap-2 text-sm text-muted-foreground disabled:cursor-not-allowed disabled:opacity-70 mt-4"
                >
                  + Add New Committee Details
                </button>
              </div>

              {hasSavedSCNMeeting && (
                <div className="mt-6">
                  <ViewOrAddRecommendation
                    caseId={case_id!}
                    scrollRef={scrollRef}
                    actionAgainstInvolvedPartiesDisabled={false}
                    processAndPolicyImprovementsDisabled={true}
                    hidePolicyImprovements={true}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {!isPageLoading && case_id && (
        <div className="w-full mx-auto max-w-8xl px-6">
          <CommentMainPage
            caseId={case_id}
            threadType={CommentThreadType.PRE_SCN_FIC}
            entityId={case_id}
            entityType={CommentEntityType.CASE}
            className="w-full max-w-none mx-0 mt-4"
          />
        </div>
      )}

      <div className="h-14" />
    </div>
  );
};

export default PreSCNFicPage;
